// src/components/PackingLists/ItemsTable.js
import React, { useState, useMemo } from 'react';
import { Button, Form } from 'react-bootstrap';

/**
 * ItemsTable component displays a table of packing list items
 * 
 * @param {Object} props - Component props
 * @param {Array} props.items - List of items to display
 * @param {string} props.tableTitle - Optional title for the table
 * @param {Object} props.collapsedGroups - Object tracking which groups are collapsed
 * @param {Function} props.toggleGroupCollapse - Function to toggle a group's collapse state
 * @param {string} props.viewDensity - Current view density ('relaxed', 'compressed')
 * @param {string} props.groupBy - Current groupBy setting ('none', 'category', 'location')
 * @param {boolean} props.isOwner - Whether the current user is the list owner
 * @param {Function} props.togglePacked - Function to toggle an item's packed status
 * @param {Function} props.saveItem - Function to save an edited item
 * @param {Function} props.deleteItem - Function to delete an item
 * @param {string} props.editingItemId - ID of the item currently being edited
 * @param {Function} props.setEditingItemId - Function to set the editing item ID
 * @param {Object} props.list - The complete list data
 * @param {Function} props.setList - Function to update list data
 * @param {Array} props.storageLocations - Available storage locations
 * @param {Array} props.categories - Available categories
 * @returns {JSX.Element} Rendered component
 */
const ItemsTable = ({
  items,
  tableTitle = null,
  collapsedGroups,
  toggleGroupCollapse,
  viewDensity,
  groupBy,
  isOwner,
  togglePacked,
  saveItem,
  deleteItem,
  editingItemId,
  setEditingItemId,
  list,
  setList,
  storageLocations,
  categories
}) => {
    const isCollapsed = tableTitle ? collapsedGroups[tableTitle] : false;
    
    // Sorting state
    const [sortColumn, setSortColumn] = useState('name'); // Default sort by item name
    const [sortDirection, setSortDirection] = useState('asc'); // 'asc' or 'desc'
    
    // Toggle sort column and direction
    const toggleSort = (column) => {
      if (sortColumn === column) {
        // Toggle direction if same column is clicked
        setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
      } else {
        // Set new column and default to ascending
        setSortColumn(column);
        setSortDirection('asc');
      }
    };
    
    // Get sort indicator for column header
    const getSortIndicator = (column) => {
      if (sortColumn === column) {
        return sortDirection === 'asc' ? ' ▲' : ' ▼';
      }
      return '';
    };
    
    // Sort items based on current sort settings
    const sortedItems = useMemo(() => {
      if (!items) return [];
      
      return [...items].sort((a, b) => {
        let valueA, valueB;
        
        if (sortColumn === 'name') {
          valueA = a.name.toLowerCase();
          valueB = b.name.toLowerCase();
        } else if (sortColumn === 'category') {
          valueA = (a.category || '').toLowerCase();
          valueB = (b.category || '').toLowerCase();
        } else if (sortColumn === 'location') {
          valueA = (a.location || '').toLowerCase();
          valueB = (b.location || '').toLowerCase();
        } else {
          return 0;
        }
        
        if (valueA < valueB) {
          return sortDirection === 'asc' ? -1 : 1;
        }
        if (valueA > valueB) {
          return sortDirection === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }, [items, sortColumn, sortDirection]);
  
    return (
      <div className="card mb-4">
        {tableTitle && (
          <div className="card-header py-2">
            <h5 
              className="mb-0 d-flex align-items-center" 
              onClick={() => toggleGroupCollapse(tableTitle)}
              style={{ cursor: 'pointer' }}
            >
              <span className={`me-2 ${isCollapsed ? '' : 'rotate-90'}`} style={{ transition: 'transform 0.2s' }}>
                {isCollapsed ? '▶' : '▼'}
              </span>
              {tableTitle} <span className="ms-2 text-secondary small fw-normal">({items.length})</span>
            </h5>
          </div>
        )}
        
        {!isCollapsed && (
          <>
            {/* For screens >= 768px */}
            <div className="d-none d-md-block">
              <div className="table-responsive">
                <table className={`table table-hover ${viewDensity === 'compressed' ? 'table-sm' : ''}`}>
                  <thead>
                    <tr>
                      <th className="packed-col">Packed</th>
                      <th 
                        onClick={() => toggleSort('name')}
                        style={{ cursor: 'pointer' }}
                        role="button"
                        className={`name-col ${sortColumn === 'name' ? 'bg-light' : ''}`}
                      >
                        <div className="d-flex align-items-center">
                          Item
                          {sortColumn === 'name' && (
                            <i className={`ms-2 bi bi-arrow-${sortDirection === 'asc' ? 'up' : 'down'}`}></i>
                          )}
                        </div>
                      </th>
                      <th className="qty-col">Qty</th>
                      {groupBy !== 'category' && (
                        <th 
                          onClick={() => toggleSort('category')}
                          style={{ cursor: 'pointer' }}
                          role="button"
                          className={`category-col ${sortColumn === 'category' ? 'bg-light' : ''}`}
                        >
                          <div className="d-flex align-items-center">
                            Category
                            {sortColumn === 'category' && (
                              <i className={`ms-2 bi bi-arrow-${sortDirection === 'asc' ? 'up' : 'down'}`}></i>
                            )}
                          </div>
                        </th>
                      )}
                      {groupBy !== 'location' && (
                        <th 
                          onClick={() => toggleSort('location')}
                          style={{ cursor: 'pointer' }}
                          role="button"
                          className={`location-col ${sortColumn === 'location' ? 'bg-light' : ''}`}
                        >
                          <div className="d-flex align-items-center">
                            Location
                            {sortColumn === 'location' && (
                              <i className={`ms-2 bi bi-arrow-${sortDirection === 'asc' ? 'up' : 'down'}`}></i>
                            )}
                          </div>
                        </th>
                      )}
                      {isOwner && <th className="actions-col">Actions</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {sortedItems.map(item => {
                      const isEditing = editingItemId === item.id;
  
                      return (
                        <tr key={item.id}>
                          <td>
                            <div className="form-check">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                checked={item.packed}
                                onChange={() => togglePacked(item.id)}
                                disabled={!isOwner || isEditing}
                              />
                            </div>
                          </td>
                          <td className={item.packed ? 'text-decoration-line-through' : ''}>
                            {isEditing ? (
                              <input
                                type="text"
                                className="form-control"
                                value={item.name}
                                onChange={(e) => {
                                  const newName = e.target.value;
                                  setList(prevList => ({
                                    ...prevList,
                                    items: prevList.items.map(i =>
                                      i.id === item.id ? { ...i, name: newName } : i
                                    )
                                  }));
                                }}
                              />
                            ) : (
                              item.name
                            )}
                          </td>
                          <td>
                            {isEditing ? (
                              <input
                                type="number"
                                className="form-control"
                                value={item.quantity}
                                onChange={(e) => {
                                  const newQuantity = parseInt(e.target.value);
                                  setList(prevList => ({
                                    ...prevList,
                                    items: prevList.items.map(i =>
                                      i.id === item.id ? { ...i, quantity: newQuantity } : i
                                    )
                                  }));
                                }}
                              />
                            ) : (
                              item.quantity
                            )}
                          </td>
                          {/* Category column */}
                          {groupBy !== 'category' && (
                            <td>
                              {isEditing ? (
                                <select
                                  className="form-select"
                                  value={item.category}
                                  onChange={(e) => {
                                    const newCategory = e.target.value;
                                    setList(prevList => ({
                                      ...prevList,
                                      items: prevList.items.map(i =>
                                        i.id === item.id ? { ...i, category: newCategory } : i
                                      )
                                    }));
                                  }}
                                >
                                  <option value="">Select Category</option>
                                  {categories.map(category => (
                                    <option key={category.id} value={category.name}>
                                      {category.name}
                                    </option>
                                  ))}
                                  <option value="add_new">+ Add New</option>
                                </select>
                              ) : (
                                item.category || '-'
                              )}
                            </td>
                          )}
                          {/* Location column */}
                          {groupBy !== 'location' && (
                            <td>
                              {isEditing ? (
                                <select
                                  className="form-select"
                                  value={item.location}
                                  onChange={(e) => {
                                    const newLocation = e.target.value;
                                    setList(prevList => ({
                                      ...prevList,
                                      items: prevList.items.map(i =>
                                        i.id === item.id ? { ...i, location: newLocation } : i
                                      )
                                    }));
                                  }}
                                >
                                  <option value="">Select Location</option>
                                  {storageLocations.map(location => (
                                    <option key={location.id} value={location.name}>
                                      {location.name}
                                    </option>
                                  ))}
                                  <option value="add_new">+ Add New</option>
                                </select>
                              ) : (
                                item.location || '-'
                              )}
                            </td>
                          )}
                          {/* Actions column */}
                          {isOwner && (
                            <td>
                              {isEditing ? (
                                <div className="d-flex gap-2">
                                  <Button
                                    variant="primary"
                                    size="sm"
                                    onClick={() => {
                                      const itemIndex = list.items.findIndex(i => i.id === item.id);
                                      if (itemIndex === -1) return;
                                      saveItem(item.id, list.items[itemIndex]);
                                    }}
                                  >
                                    Save
                                  </Button>
                                  <Button
                                    variant="secondary"
                                    size="sm"
                                    onClick={() => setEditingItemId(null)}
                                  >
                                    Cancel
                                  </Button>
                                </div>
                              ) : (
                                <div className="d-flex gap-2">
                                  <Button 
                                    variant="outline-primary" 
                                    size="sm" 
                                    onClick={() => setEditingItemId(item.id)}
                                  >
                                    Edit
                                  </Button>
                                  <Button 
                                    variant="outline-danger" 
                                    size="sm" 
                                    onClick={() => deleteItem(item.id)}
                                  >
                                    Delete
                                  </Button>
                                </div>
                              )}
                            </td>
                          )}
                        </tr>
                      );
                    })}
                    {items.length === 0 && (
                      <tr>
                        <td colSpan={
                          isOwner ? 
                            (groupBy === 'none' ? 6 : 5) : 
                            (groupBy === 'none' ? 5 : 4)
                        } className="text-center text-muted py-4">
                          No items in this group.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
  
            {/* For screens < 768px (mobile) */}
            <div className="d-md-none">
              {/* Mobile sort controls */}
              {items && items.length > 0 && (
                <div className="p-3 border-bottom bg-light">
                  <div className="d-flex align-items-center">
                    <span className="fw-medium text-secondary me-2">Sort by:</span>
                    <div className="d-flex flex-grow-1 gap-2">
                      <Form.Select 
                        size="sm"
                        value={sortColumn}
                        onChange={(e) => {
                          setSortColumn(e.target.value);
                          setSortDirection('asc');
                        }}
                      >
                        <option value="name">Item</option>
                        {groupBy !== 'category' && <option value="category">Category</option>}
                        {groupBy !== 'location' && <option value="location">Location</option>}
                      </Form.Select>
                      <Button
                        variant={sortDirection === 'asc' ? 'outline-primary' : 'primary'}
                        size="sm"
                        onClick={() => setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')}
                      >
                        <i className={`bi bi-sort-alpha-${sortDirection === 'asc' ? 'down' : 'up'}`}></i>
                      </Button>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Items display */}
              {items.length === 0 ? (
                <div className="card-body text-center text-muted py-4">
                  No items in this group.
                </div>
              ) : (
                <div className={`px-2 py-2 ${viewDensity === 'compressed' ? 'gap-2' : 'gap-3'}`}>
                  {sortedItems.map(item => {
                    const isEditing = editingItemId === item.id;
                    
                    return (
                      <div key={item.id} className={`card mb-2 ${item.packed ? 'border-opacity-50 bg-secondary-subtle' : ''}`}>
                        <div className="card-body p-3">
                          {/* Primary row: checkbox, name, quantity */}
                          <div className="d-flex align-items-center mb-2">
                            <div className="form-check me-2">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                checked={item.packed}
                                onChange={() => togglePacked(item.id)}
                                disabled={!isOwner || isEditing}
                              />
                            </div>
                            <div className={`flex-grow-1 ${item.packed ? 'text-decoration-line-through text-muted' : ''}`}>
                              {isEditing ? (
                                <input
                                  type="text"
                                  className="form-control"
                                  value={item.name}
                                  onChange={(e) => {
                                    const newName = e.target.value;
                                    setList(prevList => ({
                                      ...prevList,
                                      items: prevList.items.map(i =>
                                        i.id === item.id ? { ...i, name: newName } : i
                                      )
                                    }));
                                  }}
                                />
                              ) : (
                                <div className="fw-medium">{item.name}</div>
                              )}
                            </div>
                            <div className="ms-2 text-end">
                              {isEditing ? (
                                <input
                                  type="number"
                                  className="form-control"
                                  style={{ width: "70px" }}
                                  value={item.quantity}
                                  onChange={(e) => {
                                    const newQuantity = parseInt(e.target.value);
                                    setList(prevList => ({
                                      ...prevList,
                                      items: prevList.items.map(i =>
                                        i.id === item.id ? { ...i, quantity: newQuantity } : i
                                      )
                                    }));
                                  }}
                                />
                              ) : (
                                <span className="badge bg-secondary">Qty: {item.quantity}</span>
                              )}
                            </div>
                          </div>
                          
                          {/* Secondary row: category, location */}
                          {!isEditing && (
                            <div className="d-flex flex-wrap text-muted small mb-2">
                              {item.category && groupBy !== 'category' && (
                                <div className="me-3">
                                  <span className="fw-medium">Category:</span> {item.category}
                                </div>
                              )}
                              {item.location && groupBy !== 'location' && (
                                <div>
                                  <span className="fw-medium">Location:</span> {item.location}
                                </div>
                              )}
                            </div>
                          )}
                          
                          {/* Editing fields for category and location */}
                          {isEditing && (
                            <div className="row g-2 mb-2">
                              {groupBy !== 'category' && (
                                <div className="col-12">
                                  <label className="form-label small mb-1">Category</label>
                                  <select
                                    className="form-select form-select-sm"
                                    value={item.category}
                                    onChange={(e) => {
                                      const newCategory = e.target.value;
                                      setList(prevList => ({
                                        ...prevList,
                                        items: prevList.items.map(i =>
                                          i.id === item.id ? { ...i, category: newCategory } : i
                                        )
                                      }));
                                    }}
                                  >
                                    <option value="">Select Category</option>
                                    {categories.map(category => (
                                      <option key={category.id} value={category.name}>
                                        {category.name}
                                      </option>
                                    ))}
                                    <option value="add_new">+ Add New</option>
                                  </select>
                                </div>
                              )}
                              
                              {groupBy !== 'location' && (
                                <div className="col-12">
                                  <label className="form-label small mb-1">Location</label>
                                  <select
                                    className="form-select form-select-sm"
                                    value={item.location}
                                    onChange={(e) => {
                                      const newLocation = e.target.value;
                                      setList(prevList => ({
                                        ...prevList,
                                        items: prevList.items.map(i =>
                                          i.id === item.id ? { ...i, location: newLocation } : i
                                        )
                                      }));
                                    }}
                                  >
                                    <option value="">Select Location</option>
                                    {storageLocations.map(location => (
                                      <option key={location.id} value={location.name}>
                                        {location.name}
                                      </option>
                                    ))}
                                    <option value="add_new">+ Add New</option>
                                  </select>
                                </div>
                              )}
                            </div>
                          )}
                          
                          {/* Action buttons */}
                          {isOwner && (
                            <div className="d-flex justify-content-end mt-2">
                              {isEditing ? (
                                <>
                                  <Button
                                    variant="primary"
                                    size="sm"
                                    className="me-2"
                                    onClick={() => {
                                      const itemIndex = list.items.findIndex(i => i.id === item.id);
                                      if (itemIndex === -1) return;
                                      saveItem(item.id, list.items[itemIndex]);
                                    }}
                                  >
                                    Save
                                  </Button>
                                  <Button
                                    variant="secondary"
                                    size="sm"
                                    onClick={() => setEditingItemId(null)}
                                  >
                                    Cancel
                                  </Button>
                                </>
                              ) : (
                                <>
                                  <Button 
                                    variant="outline-primary" 
                                    size="sm"
                                    className="me-2"
                                    onClick={() => setEditingItemId(item.id)}
                                  >
                                    Edit
                                  </Button>
                                  <Button 
                                    variant="outline-danger" 
                                    size="sm" 
                                    onClick={() => deleteItem(item.id)}
                                  >
                                    Delete
                                  </Button>
                                </>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    );
  };

export default ItemsTable;